import { paymentManualApproval } from '@otter-ai/recall-client';
import { Appearance } from '@stripe/stripe-js';
import { useCallback, useState } from 'react';
import { stripeDefaultAppearance, stripeErrorAppearance } from '#constants';

type UseManualApprovalParams = {
  checkoutSessionId: string;
  country: string;
  changeAppearance: (appearance: Appearance) => void;
};

export function useManualApproval({
  checkoutSessionId,
  country,
  changeAppearance,
}: UseManualApprovalParams) {
  const [hasIndiaPaymentError, setHasIndiaPaymentError] = useState(false);

  const onRequiresApproval = useCallback(async (): Promise<boolean> => {
    const setDeniedState = () => {
      changeAppearance(stripeErrorAppearance);
      setHasIndiaPaymentError(true);
    };

    try {
      const manualApprovalResult = await paymentManualApproval({
        body: {
          checkout_session_id: checkoutSessionId,
          user_details: {
            country,
          },
        },
      });

      if (manualApprovalResult.error) {
        setDeniedState();
        return false;
      }

      if (manualApprovalResult?.data?.is_manually_approved === false) {
        setDeniedState();
      }

      return manualApprovalResult.data?.is_manually_approved ?? false;
    } catch {
      setDeniedState();
      return false;
    }
  }, [checkoutSessionId, country, changeAppearance]);

  const clearIndiaPaymentError = useCallback(() => {
    if (hasIndiaPaymentError) {
      changeAppearance(stripeDefaultAppearance);
      setHasIndiaPaymentError(false);
    }
  }, [hasIndiaPaymentError, changeAppearance]);

  return {
    hasIndiaPaymentError,
    onRequiresApproval,
    clearIndiaPaymentError,
  };
}
